/**
 * Gamepad support via the Gamepad API.
 *
 * Detection is polled rather than event-driven because the browser only populates axis and
 * button values on `navigator.getGamepads()`, and only after the user has touched the device.
 * A connected-but-untouched pad reports all zeros, so "is a gamepad in use" means *a gamepad
 * that has actually moved*, not merely one that is plugged in — otherwise a dormant pad in a
 * drawer would fight the keyboard for control of the aeroplane.
 *
 * `updateFrame(alpha)`: yes — polled once per frame, since the browser only refreshes the
 * snapshot when it is read.
 */

import { shapeAxis, type AxisShapingOptions } from './axes.js';

/** Standard-layout axis indices. */
export const GAMEPAD_AXIS = {
  leftStickX: 0,
  leftStickY: 1,
  rightStickX: 2,
  rightStickY: 3,
} as const;

/** Standard-layout button indices used by the flight controls. */
export const GAMEPAD_BUTTON = {
  a: 0,
  b: 1,
  x: 2,
  y: 3,
  leftBumper: 4,
  rightBumper: 5,
  leftTrigger: 6,
  rightTrigger: 7,
  dpadUp: 12,
  dpadDown: 13,
  dpadLeft: 14,
  dpadRight: 15,
} as const;

export interface GamepadMapping {
  roll: number;
  pitch: number;
  yaw: number;
  /** Axis used for throttle; combined with the trigger buttons when present. */
  throttle: number;
  shaping: {
    roll: AxisShapingOptions;
    pitch: AxisShapingOptions;
    yaw: AxisShapingOptions;
  };
}

export const DEFAULT_GAMEPAD_MAPPING: GamepadMapping = {
  roll: GAMEPAD_AXIS.leftStickX,
  pitch: GAMEPAD_AXIS.leftStickY,
  yaw: GAMEPAD_AXIS.rightStickX,
  throttle: GAMEPAD_AXIS.rightStickY,
  shaping: {
    // Deadzones sized for a worn analogue stick; expo because a thumbstick has very little
    // travel and needs the middle stretched out to be flyable.
    roll: { deadzone: 0.08, expo: 0.35 },
    pitch: { deadzone: 0.08, expo: 0.35, invert: true },
    yaw: { deadzone: 0.12, expo: 0.4 },
  },
};

export interface GamepadState {
  connected: boolean;
  /** True once the player has actually moved something. */
  active: boolean;
  id: string;
  roll: number;
  pitch: number;
  yaw: number;
  /** Absolute throttle position, 0…1, or null when the pad is not driving it. */
  throttle: number | null;
  buttons: boolean[];
  /** Buttons that went down since the last poll. */
  pressed: boolean[];
}

const MAX_BUTTONS = 20;

/** Returned when the browser has no gamepad API at all, so the loop below has something to walk. */
const EMPTY_PADS: readonly (Gamepad | null)[] = [];

export class GamepadInput {
  private readonly mapping: GamepadMapping;
  private readonly previousButtons: boolean[] = new Array<boolean>(MAX_BUTTONS).fill(false);

  readonly state: GamepadState = {
    connected: false,
    active: false,
    id: '',
    roll: 0,
    pitch: 0,
    yaw: 0,
    throttle: null,
    buttons: new Array<boolean>(MAX_BUTTONS).fill(false),
    pressed: new Array<boolean>(MAX_BUTTONS).fill(false),
  };

  /** Throttle is integrated from the stick so it behaves like a lever, not a spring. */
  private throttlePosition = 0;

  /**
   * Pads the browser has told us about. See {@link updateFrame} for why the poll is gated on it.
   *
   * Counted rather than a boolean because two pads can connect and one disconnect, and a boolean
   * would switch polling off while a pad was still plugged in.
   */
  private connectedCount = 0;
  private listening = false;

  private readonly onConnected = (): void => {
    this.connectedCount++;
  };

  private readonly onDisconnected = (): void => {
    this.connectedCount = Math.max(0, this.connectedCount - 1);
  };

  constructor(mapping: GamepadMapping = DEFAULT_GAMEPAD_MAPPING) {
    this.mapping = mapping;
    this.attach();
  }

  /**
   * Start listening for pads. Idempotent, and safe where the events do not exist.
   *
   * A pad already connected when the page loads does not fire `gamepadconnected` until it is
   * touched — that is the specified behaviour, and it is why the *first* poll is unconditional:
   * one `getGamepads()` call at start-up costs nothing and finds a pad that is already there.
   */
  attach(): void {
    if (this.listening) return;
    this.listening = true;
    // One unconditional look, because a pad plugged in before load stays silent until used.
    const pads = globalThis.navigator?.getGamepads?.();
    if (pads !== undefined) {
      for (const candidate of pads) if (candidate !== null && candidate.connected) this.connectedCount++;
    }
    globalThis.addEventListener?.('gamepadconnected', this.onConnected);
    globalThis.addEventListener?.('gamepaddisconnected', this.onDisconnected);
  }

  detach(): void {
    if (!this.listening) return;
    this.listening = false;
    globalThis.removeEventListener?.('gamepadconnected', this.onConnected);
    globalThis.removeEventListener?.('gamepaddisconnected', this.onDisconnected);
  }

  /** Poll the pad. Call once per frame. */
  updateFrame(dtSeconds: number): GamepadState {
    const state = this.state;

    /*
     * `getGamepads()` builds a fresh array — and a fresh `Gamepad` snapshot per slot — on every
     * call, by specification. Called once a frame with no pad plugged in, that is four nulls in a
     * new array 120 times a second for a feature nobody is using.
     *
     * So it is only called when the browser has told us a pad exists. `gamepadconnected` and
     * `gamepaddisconnected` are exactly the events for that, they are already how the API expects
     * to be driven, and between them the poll is skipped entirely. With a pad connected the
     * behaviour is unchanged, because that is when the snapshot is the point.
     */
    let pad: Gamepad | null = null;
    if (this.connectedCount > 0) {
      const pads = globalThis.navigator?.getGamepads?.() ?? EMPTY_PADS;
      for (const candidate of pads) {
        if (candidate !== null && candidate.connected) {
          pad = candidate;
          break;
        }
      }
    }

    if (pad === null) {
      state.connected = false;
      state.active = false;
      state.throttle = null;
      state.roll = 0;
      state.pitch = 0;
      state.yaw = 0;
      state.pressed.fill(false);
      return state;
    }

    state.connected = true;
    state.id = pad.id;

    const mapping = this.mapping;
    const rawRoll = pad.axes[mapping.roll] ?? 0;
    const rawPitch = pad.axes[mapping.pitch] ?? 0;
    const rawYaw = pad.axes[mapping.yaw] ?? 0;
    const rawThrottleAxis = pad.axes[mapping.throttle] ?? 0;

    state.roll = shapeAxis(rawRoll, mapping.shaping.roll);
    state.pitch = shapeAxis(rawPitch, mapping.shaping.pitch);
    state.yaw = shapeAxis(rawYaw, mapping.shaping.yaw);

    let anyButtonDown = false;
    for (let i = 0; i < MAX_BUTTONS; i++) {
      const down = pad.buttons[i]?.pressed === true;
      state.pressed[i] = down && !this.previousButtons[i];
      state.buttons[i] = down;
      this.previousButtons[i] = down;
      if (down) anyButtonDown = true;
    }

    // Throttle: the right stick's vertical axis drives it like a lever, and the bumpers nudge.
    const throttleRate = 0.8;
    const stickDemand = shapeAxis(rawThrottleAxis, { deadzone: 0.15, invert: true });
    const bumperDemand =
      (state.buttons[GAMEPAD_BUTTON.rightBumper] ? 1 : 0) -
      (state.buttons[GAMEPAD_BUTTON.leftBumper] ? 1 : 0);
    const demand = bumperDemand !== 0 ? bumperDemand : stickDemand;
    this.throttlePosition = Math.min(1, Math.max(0, this.throttlePosition + demand * throttleRate * dtSeconds));

    // "Active" means the player has actually touched it — a pad sitting untouched must not
    // hold the throttle at zero and fight the keyboard.
    const stickMoved =
      Math.abs(rawRoll) > 0.2 || Math.abs(rawPitch) > 0.2 || Math.abs(rawYaw) > 0.2 || Math.abs(rawThrottleAxis) > 0.2;
    if (stickMoved || anyButtonDown) state.active = true;

    state.throttle = state.active ? this.throttlePosition : null;
    return state;
  }

  /** Sync the pad's throttle lever to an external value, e.g. after keyboard input. */
  setThrottle(value: number): void {
    this.throttlePosition = Math.min(1, Math.max(0, value));
  }
}
